// ---------------------------------------------------------------------------
// BeTheFirstCallout.js
// ---------------------------------------------------------------------------
// Reusable promo banner used on the home page + state / region landing
// pages. Its job is to promote the FIRST-MOVER advantage: "Be the FIRST
// Dog Trainer or Dog Training Facility in your STATE or REGION with an
// advantage over the rest by creating your K9os.app."
//
// We keep this component tiny and prop-driven so each host page can tweak
// the audience noun ("state" vs "region" vs "city") and the CTA target
// without duplicating the JSX. No implication of active adoption
// anywhere in this copy — the message is explicitly "get in before the
// competition does."
// ---------------------------------------------------------------------------
import React from 'react';
import { Link } from 'react-router-dom';
import { Trophy, ArrowRight, Sparkles } from 'lucide-react';

export const BeTheFirstCallout = ({
  // What to promote first-mover status in. Renders inside the headline as
  // "in your <scopeLabel>" — e.g. "your STATE", "your REGION", "your CITY".
  scopeLabel = 'state or region',
  // Optional area-specific line to append to the copy (e.g. "in Texas").
  // Leaving this blank keeps the callout usable on the homepage.
  areaSuffix = '',
  // Where the primary CTA button routes to. Defaults to the demo booking.
  ctaHref = '/contact',
  ctaLabel = 'Claim your city first',
  // Visual variant — light/default sits on white backgrounds, dark works
  // on the dark hero gradients (state/region landing heros).
  variant = 'default',
  className = '',
  testid = 'be-the-first-callout',
}) => {
  const box =
    variant === 'dark'
      ? 'bg-[#0B1220]/80 border-white/15 text-white'
      : 'bg-gradient-to-r from-[#C8102E]/8 via-white to-[#1B3A87]/8 dark:from-[#C8102E]/20 dark:via-slate-900 dark:to-[#1B3A87]/20 border-slate-200 dark:border-slate-800 text-[#0B1220] dark:text-slate-100';
  const eyebrowColor = variant === 'dark' ? 'text-[#F87171]' : 'text-[#C8102E]';
  const trophyColor  = variant === 'dark' ? 'text-[#F59E0B]' : 'text-[#C8102E]';
  const bodyColor    = variant === 'dark' ? 'text-slate-200' : 'text-slate-700 dark:text-slate-200';
  const ctaClass =
    variant === 'dark'
      ? 'bg-white text-[#0B1220] hover:bg-slate-100'
      : 'bg-[#C8102E] hover:bg-[#A50D26] text-white';

  return (
    <aside
      className={`relative overflow-hidden rounded-2xl border ${box} p-5 sm:p-6 shadow-md ${className}`}
      data-testid={testid}
    >
      {/* Decorative sparkle blob — subtle brand accent, non-interactive. */}
      <div
        aria-hidden='true'
        className='pointer-events-none absolute -top-10 -right-10 w-40 h-40 rounded-full bg-[#F59E0B]/15 blur-2xl'
      />
      <div className='relative flex flex-col md:flex-row md:items-center gap-4 md:gap-6'>
        <div className={`shrink-0 w-11 h-11 rounded-xl bg-white/10 border border-white/10 flex items-center justify-center ${trophyColor}`}>
          <Trophy className='w-6 h-6' />
        </div>
        <div className='flex-1 min-w-0'>
          <div className={`text-[10px] uppercase tracking-[0.22em] font-bold ${eyebrowColor} inline-flex items-center gap-1.5`}>
            <Sparkles className='w-3 h-3' /> First-mover advantage
          </div>
          <h3 className='mt-1 font-display text-lg sm:text-xl md:text-2xl font-bold leading-tight'>
            Be the <span className={eyebrowColor}>FIRST</span> Dog Trainer or Dog Training Facility in your {scopeLabel}
            {areaSuffix ? ` ${areaSuffix}` : ''} with your own K9os-powered app.
          </h3>
          <p className={`mt-2 text-sm sm:text-base leading-relaxed ${bodyColor}`}>
            Get an advantage over the rest of the local competition &mdash; a custom SEO-optimised website, kennel bookings, board-and-train onboarding, POS, memberships, contracts, messaging, push notifications, and marketing, all under one login built for your business.
          </p>
        </div>
        <div className='shrink-0'>
          <Link
            to={ctaHref}
            className={`inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-full font-bold text-sm shadow-md hover:-translate-y-0.5 transition-all ${ctaClass}`}
            data-testid={`${testid}-cta`}
          >
            {ctaLabel} <ArrowRight className='w-4 h-4' />
          </Link>
        </div>
      </div>
    </aside>
  );
};

export default BeTheFirstCallout;
